import { ErrorMessage } from '@hookform/error-message';
import {
  Box,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Error } from 'components/Common/Error';
import { format } from 'date-fns';
import { FC, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DATE_FORMAT_API } from '../../../../../../../../@constants/date';
import DateRangePicker from '../../../../../../../../components/DateRangePicker';
import { Calendar2Icon } from '../../../../../../../../components/SvgIcons/Calendar2Icon';
import { FileExcelIcon } from '../../../../../../../../components/SvgIcons/FileExcelIcon';
import { FilePdfIcon } from '../../../../../../../../components/SvgIcons/FilePdfIcon';
import { FooterExtraButtons } from '../../../../../../components/FooterButtons';
import { RateField } from '../../../../../../components/Inputs/RateField';
import { RoomTypeField } from '../../../../../../components/Inputs/RoomTypeField';
import { ReportLabel } from '../../../../../../components/ReportLabel';
import { DailyForecastReportFormModel } from '../../models';
import { CompanyField } from './fields/CompanyField';
import { CurrencyApplicationField } from './fields/currencyApplicationField';
import { CurrencyField } from './fields/CurrencyField';
import { FixedAtField } from './fields/FixedAtField';

export const FilterForm: FC<{
  onSubmit: (data: DailyForecastReportFormModel) => void;
  onReset: () => void;
}> = ({ onSubmit, onReset }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<DailyForecastReportFormModel>();

  const formValues = watch();

  const dates = useMemo(
    (): (Date | null)[] => [
      formValues?.from ? new Date(formValues?.from) : null,
      formValues?.to ? new Date(formValues?.to) : null,
    ],
    [formValues?.from, formValues?.to]
  );

  const handleChangeDates = (value: (Date | null)[]) => {
    const [from, to] = value;
    const _from = from ? format(from, DATE_FORMAT_API) : null;
    const _to = to ? format(to, DATE_FORMAT_API) : null;
    setValue('from', _from);
    setValue('to', _to);
  };

  return (
    <Box sx={{ p: 0, height: '100%', backgroundColor: 'colors.gray.o10', overflowY: 'scroll' }}>
      <Stack direction={'column'} spacing={0} sx={{ width: '100%', height: '100%' }}>
        <ReportLabel label="Daily forecast" />

        <Stack direction={'column'} spacing={2} p={3}>
          <Stack direction={'row'} spacing={2} width={'100%'}>
            <Box sx={{ width: '100%' }}>
              <DateRangePicker
                value={dates}
                onChange={handleChangeDates}
                renderInput={({ startProps, endProps }) => (
                  <Stack
                    direction="row"
                    spacing="1rem"
                    sx={{
                      width: '100%',
                    }}
                  >
                    <TextField
                      {...startProps}
                      size="medium"
                      variant="standard"
                      sx={{
                        width: '50%',
                      }}
                      label="From"
                      error={Boolean(errors?.from)}
                      helperText={errors?.from?.message}
                      placeholder="mm/dd/yyyy"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Calendar2Icon
                              sx={{
                                fontSize: '1rem',
                                color: ({ palette }) => palette.common.black,
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      {...endProps}
                      size="medium"
                      variant="standard"
                      sx={{
                        width: '50%',
                      }}
                      label="To"
                      error={Boolean(errors?.to)}
                      helperText={errors?.to?.message}
                      placeholder="mm/dd/yyyy"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Calendar2Icon
                              sx={{
                                fontSize: '1rem',
                                color: ({ palette }) => palette.common.black,
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Stack>
                )}
              />
            </Box>
          </Stack>

          <Controller
            control={control}
            name="company"
            render={({ field }) => (
              <CompanyField value={field.value} onChange={value => field.onChange(value)} />
            )}
          />

          <Controller
            control={control}
            name="rates"
            render={({ field, formState }) => (
              <RateField
                value={field.value}
                onChange={value => field.onChange(value)}
                error={
                  formState?.errors[field.name] ? (
                    <ErrorMessage
                      errors={formState.errors}
                      name={field.name}
                      render={({ message }) => <Error>{message}</Error>}
                    />
                  ) : null
                }
              />
            )}
          />
          <Controller
            control={control}
            name="roomTypeIds"
            render={({ field }) => (
              <RoomTypeField value={field.value} onChange={value => field.onChange(value)} />
            )}
          />

          <Box sx={{ height: 10 }} />

          <Controller
            control={control}
            name="with_revenue"
            render={({ field }) => (
              <FormControlLabel
                sx={{ m: 0 }}
                {...field}
                checked={field.value ?? false}
                onChange={(e, checked) => field.onChange(checked)}
                label={
                  <Typography variant="inter16" fontWeight={500} sx={{ ml: '1rem' }}>
                    With Revenue
                  </Typography>
                }
                control={<Switch />}
              />
            )}
          />

          <Controller
            control={control}
            name="currency"
            render={({ field, formState }) => (
              <CurrencyField
                value={field.value}
                onChange={value => field.onChange(value)}
                error={
                  formState?.errors[field.name] ? (
                    <ErrorMessage
                      errors={formState.errors}
                      name={field.name}
                      render={({ message }) => <Error>{message}</Error>}
                    />
                  ) : null
                }
              />
            )}
          />

          <Controller
            control={control}
            name="currency_application"
            render={({ field, formState }) => (
              <CurrencyApplicationField
                value={field.value}
                onChange={value => field.onChange(value)}
                error={
                  formState?.errors[field.name] ? (
                    <ErrorMessage
                      errors={formState.errors}
                      name={field.name}
                      render={({ message }) => <Error>{message}</Error>}
                    />
                  ) : null
                }
              />
            )}
          />

          <Controller
            control={control}
            name="fixed_at"
            render={({ field, formState }) => (
              <FixedAtField
                value={field.value}
                onChange={value => field.onChange(value)}
                helperText={
                  <>
                    <ErrorMessage
                      errors={formState.errors}
                      name={field.name}
                      render={({ message }) => <Error>{message}</Error>}
                    />
                  </>
                }
              />
            )}
          />

          <Controller
            control={control}
            name="gross_revenue"
            render={({ field }) => (
              <FormControlLabel
                disabled={formValues.with_revenue === false}
                sx={{ m: 0 }}
                {...field}
                checked={field.value ?? false}
                onChange={(e, checked) => field.onChange(checked)}
                label={
                  <Typography variant="inter16" fontWeight={500} sx={{ ml: '1rem' }}>
                    Gross Revenue
                  </Typography>
                }
                control={<Switch />}
              />
            )}
          />
        </Stack>
        {/* <FooterButtons onSubmit={handleSubmit(onSubmit)} onReset={onReset} /> */}

        <FooterExtraButtons
          onSubmit={file_type => {
            setValue('file_type', file_type);
            handleSubmit(onSubmit)();
          }}
          onReset={onReset}
          exportFileTypes={[
            {
              label: 'PDF',
              value: 'pdf',
              icon: <FilePdfIcon sx={{ width: '3rem', height: '3rem' }} />,
            },
            {
              label: 'EXCEL',
              value: 'excel',
              icon: <FileExcelIcon sx={{ width: '3rem', height: '3rem' }} />,
            },
          ]}
        />
      </Stack>
    </Box>
  );
};
